import { Toggle } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Alert, Button, Modal } from 'react-bootstrap' // Adjust the import path if necessary
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../../../_augmentt/selectors/customers'
import { createHubTask, pollTaskStatus } from '../../../../../crud/integrationHub.crud'
import useFreshEmployeeData from '../../../../../hooks/useFreshEmployeeData'
import Spinner from '../../../../../partials/content/Spinner'
import { TASK_ACTIONS, TASK_TYPES } from '../../../../../util/hub-task-management/hub.constants'
import { getEmployeeFullName } from '../../../../../util/table.util'

const propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onUpdate: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  employees: PropTypes.array,
}

function GlobalAddressListModal(props) {
  const { show = false, onHide = () => {}, onUpdate = () => {}, employees = [] } = props ?? {}

  const currentUser = useSelector(({ user }) => user.data)
  const selectedCustomer = useSelector(getSelectedCustomer)
  const selectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)

  const STATUS = {
    REVIEW: 'REVIEW',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
  }

  const ERRORS = {
    GLOBAL_ERRORS: 'GLOBAL_ERRORS',
    OPERATIONAL: 'OPERATIONAL',
  }

  const [taskErrors, setTaskErrors] = useState({})
  const [status, setStatus] = useState(STATUS.REVIEW)
  const [hiddenFromAddressList, setHiddenFromAddressList] = useState({})

  // Ensure employees is always an array
  let employeesArray = []
  if (Array.isArray(employees)) {
    employeesArray = employees
  } else if (employees) {
    employeesArray = [employees]
  }

  // Fetch fresh employee data when opened from table
  const freshEmployees = useFreshEmployeeData({
    employee: employeesArray,
    show,
    customer: selectedCustomer,
    isStaleData: (emp) => !(emp?.isUserMailBoxAvailable || emp?.isUserSharedMailbox),
  })

  // check if first employee item contains hiddenFromAddressListsEnabled filed that can be true or false or undefined or null
  const hasHiddenFromAddressListField = freshEmployees?.some((employee) => {
    const { hiddenFromAddressListsEnabled } = employee
    return hiddenFromAddressListsEnabled !== undefined
  })

  useEffect(() => {
    if (freshEmployees?.length) {
      const updatedHiddenFromAddressList = freshEmployees.reduce((acc, employee) => {
        if (employee?.isUserMailBoxAvailable || employee?.isUserSharedMailbox) {
          acc[employee.employee_identifier || employee.employeeIdentifier] = employee.hiddenFromAddressListsEnabled
        }
        return acc
      }, {})

      setHiddenFromAddressList(updatedHiddenFromAddressList)
    }
  }, [freshEmployees])

  const onApply = async () => {
    setStatus(STATUS.IN_PROGRESS)

    const parameters = {
      actions: {
        // employeeIdentifiers: employees?.map((emp) => emp.employeeIdentifier),
        hiddenFromAddressList,
      },
      tasktype: TASK_TYPES.globalAddressList,
      action: TASK_ACTIONS.update,
      customerId: selectedCustomer?.id,
      userId: currentUser?.id,
      organizationId: selectedCustomer?.organization,
    }

    try {
      const { data: { taskId } = {} } = await createHubTask(parameters)

      if (taskId) {
        const { data: { _source: task } = {} } =
          (await pollTaskStatus(taskId, {
            retries: 10,
            timeout: 3000,
          })) ?? {}

        setTaskErrors(JSON.parse(task?.errors || '{}'))
        setStatus(STATUS.COMPLETED)

        await onUpdate()
      }
    } catch (error) {
      setTaskErrors({
        OPERATIONAL: { message: error.message },
      })
      setStatus(STATUS.COMPLETED)
    }
  }

  return (
    <Modal className={`right ${clsx({ in: show })}`} show={show} onHide={onHide} size="lg" backdrop>
      <Modal.Header closeButton onHide={onHide}>
        <Modal.Title>
          <FormattedMessage id="ENGAGE.GLOBAL_ADDRESS_LIST" />
        </Modal.Title>
        {freshEmployees?.length === 1 && (
          <p className="mt-2 mb-1 text-body-secondary">
            {freshEmployees?.[0]?.firstName} {freshEmployees?.[0]?.lastName}
          </p>
        )}

        <p className={freshEmployees?.length === 1 ? 'mb-0 text-body-secondary' : 'mt-2 mb-0 text-body-secondary'}>
          {selectedIntegration?.IntegrationApplication?.display_name === 'M365'
            ? 'Microsoft 365'
            : selectedIntegration?.IntegrationApplication?.display_name}
        </p>
      </Modal.Header>
      <Modal.Body className={clsx('modal-body-sidebar', 'px-4')}>
        {[STATUS.REVIEW].includes(status) && (
          <>
            <p className="text-strong mb-3">
              <FormattedMessage
                id="ENGAGE.GLOBAL_ADDRESS_LIST_DESC_TITLE"
                values={{ s: freshEmployees?.length > 1 ? 'es' : '' }}
              />
            </p>
            <p className="mb-4 text-body">
              <FormattedMessage
                id="ENGAGE.GLOBAL_ADDRESS_LIST_DESC"
                values={{ s: freshEmployees?.length > 1 ? 'es' : '' }}
              />
            </p>
            {hasHiddenFromAddressListField && (
              <>
                <p className="fw-semibold mb-2 mt-2">
                  <FormattedMessage
                    id="ENGAGE.GLOBAL_ADDRESS_LIST_APPLY_PHRASE"
                    values={{ s: freshEmployees?.length > 1 ? 's' : '' }}
                  />
                </p>
                <ul className="global-address-list-employee-list mb-0 fw-bold px-4">
                  {freshEmployees
                    ?.filter((employee) => employee?.isUserMailBoxAvailable || employee?.isUserSharedMailbox)
                    .map((employee) => {
                      const employeeId = employee.employee_identifier || employee.employeeIdentifier
                      return (
                        <li key={employee.id || employeeId} className="mb-2">
                          <div className="d-flex w-100 justify-content-between align-items-center gap-2">
                            <span className="text-break pe-2">{getEmployeeFullName(employee)}</span>
                            <Toggle
                              className="flex-shrink-0"
                              checked={hiddenFromAddressList?.[employeeId] ?? false}
                              onChange={(value) => {
                                setHiddenFromAddressList((prevState) => ({
                                  ...prevState,
                                  [employeeId]: value,
                                }))
                              }}
                            />
                          </div>
                        </li>
                      )
                    })}
                </ul>
              </>
            )}
            {!hasHiddenFromAddressListField && (
              <Alert variant="secondary" className="mt-2 mb-0">
                <p className="font-bolder alert-text mb-0">
                  <FormattedMessage id="ENGAGE.GLOBAL_ADDRESS_LIST_NO_REQUIRED_FIELD" />
                </p>
              </Alert>
            )}
          </>
        )}

        {[STATUS.IN_PROGRESS].includes(status) && (
          <div className="modal-loading-centered">
            <Spinner size="small" />
            <div className="mt-4 text-strong text-color-secondary">
              <FormattedMessage id="ENGAGE.GLOBAL_ADDRESS_LIST_IN_PROGRESS" />
            </div>
          </div>
        )}

        {!taskErrors[ERRORS.GLOBAL_ERRORS] &&
          !taskErrors[ERRORS.OPERATIONAL] &&
          [STATUS.COMPLETED].includes(status) && (
            <ul className="ps-0">
              {freshEmployees?.map((employee) => {
                const employeeId = employee.employee_identifier || employee.employeeIdentifier
                const hasError = !!taskErrors[employeeId]

                return (
                  <li key={employee.id || employeeId} className="list-unstyled mt-0">
                    {hasError ? (
                      <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
                    ) : (
                      <i className="icon-xl align-text-bottom font-color-engage fas fa-circle-check me-2" />
                    )}
                    {getEmployeeFullName(employee)}

                    {hasError && (
                      <div style={{ width: '100%' }} className="font-color-danger">
                        <pre
                          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '14px' }}
                          className="font-color-danger"
                        >
                          {JSON.stringify(taskErrors[employeeId] || {}, null, 2)}
                        </pre>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}

        {taskErrors[ERRORS.OPERATIONAL] && [STATUS.COMPLETED].includes(status) && (
          <div className="font-color-danger mt-1 ps-0">
            <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
            <FormattedMessage id="GENERAL.ERROR" />
          </div>
        )}

        {taskErrors[ERRORS.GLOBAL_ERRORS] && [STATUS.COMPLETED].includes(status) && (
          <div className="font-color-danger mt-1 ps-0">
            <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
            <FormattedMessage id="GENERAL.ERROR" />
            <pre
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '14px' }}
              className="font-color-danger mt-1"
            >
              {JSON.stringify(taskErrors[ERRORS.GLOBAL_ERRORS] || {}, null, 2)}
            </pre>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className={clsx('d-flex', 'justify-content-between')}>
        <Button onClick={onHide} variant="secondary">
          <FormattedMessage id={[STATUS.REVIEW].includes(status) ? 'GENERAL.CANCEL' : 'GENERAL.CLOSE'} />
        </Button>
        {[STATUS.REVIEW].includes(status) && (
          <Button
            id="global-address-list-submit-btn"
            variant="primary"
            onClick={onApply}
            className="bg-color-engage pull-right"
            disabled={!hasHiddenFromAddressListField}
          >
            <FormattedMessage id="GENERAL.APPLY" />
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

GlobalAddressListModal.propTypes = propTypes

export default GlobalAddressListModal
